import { request } from './http';
import { config } from './config';
import type { LatLon, PlaceSuggestion } from '../domain/types';

// Address search + geocoding go through the maps cache proxy (cacheBaseUrl),
// NOT the main API. These are unauthenticated proxy endpoints in flutter_client.

type AutocompleteResponse = {
  suggestions?: Array<{
    placeId?: string;
    primaryText?: string;
    secondaryText?: string;
    fullText?: string;
    types?: string[];
  }>;
  predictions?: Array<{
    placeId?: string;
    place_id?: string;
    mainText?: string;
    secondaryText?: string;
    description?: string;
    structuredFormatting?: { mainText?: string; secondaryText?: string };
    types?: string[];
  }>;
};

export async function autocomplete(
  query: string,
  sessionToken: string,
  near?: LatLon,
): Promise<PlaceSuggestion[]> {
  if (!query.trim()) return [];
  const res = await request<AutocompleteResponse>('/places/autocomplete', {
    method: 'POST',
    baseUrl: config.cacheBaseUrl,
    auth: false,
    body: {
      input: query,
      sessionToken,
      language: config.defaultLanguage,
      ...(near ? { lat: near.lat, lon: near.lon, radius: 50000 } : {}),
    },
  });

  const suggestions = res.suggestions ?? res.predictions ?? [];

  return suggestions.map((p) => {
    const mainText =
      ('primaryText' in p ? p.primaryText : undefined) ??
      ('mainText' in p ? p.mainText : undefined) ??
      ('structuredFormatting' in p ? p.structuredFormatting?.mainText : undefined) ??
      ('description' in p ? p.description : undefined) ??
      '';
    const secondaryText =
      ('secondaryText' in p ? p.secondaryText : undefined) ??
      ('structuredFormatting' in p ? p.structuredFormatting?.secondaryText : undefined) ??
      '';
    const fullText = ('fullText' in p ? p.fullText : undefined) ?? ('description' in p ? p.description : undefined) ?? '';

    return {
      placeId: p.placeId ?? ('place_id' in p ? p.place_id : undefined) ?? '',
      mainText,
      secondaryText,
      fullText: fullText || [mainText, secondaryText].filter(Boolean).join(', '),
      types: p.types ?? [],
    };
  });
}

type PlaceDetailsResponse = {
  address?: string;
  shortAddress?: string;
  displayName?: string;
  latitude?: number;
  longitude?: number;
  placeId?: string;
  result?: { lat?: number; lon?: number; location?: { lat: number; lng?: number; lon?: number } };
  lat?: number;
  lon?: number;
};

// GET /places/details/{placeId} -> coordinates.
export async function placeDetails(placeId: string, sessionToken: string): Promise<(LatLon & { address: string }) | null> {
  const res = await request<PlaceDetailsResponse>(
    `/places/details/${encodeURIComponent(placeId)}?sessionToken=${encodeURIComponent(sessionToken)}&language=${config.defaultLanguage}`,
    { baseUrl: config.cacheBaseUrl, auth: false },
  );
  const lat = res.latitude ?? res.result?.lat ?? res.result?.location?.lat ?? res.lat;
  const lng = res.longitude ?? res.result?.lon ?? res.result?.location?.lon ?? res.result?.location?.lng ?? res.lon;
  if (typeof lat === 'number' && typeof lng === 'number') {
    return {
      lat,
      lon: lng,
      address: res.shortAddress || res.address || res.displayName || '',
    };
  }
  return null;
}

type ReverseResponse = {
  address?: string;
  display_name?: string;
  result?: { address?: string };
};

// POST /reverse-geocode-locationiq -> human address for a map pin.
export async function reverseGeocode(point: LatLon): Promise<string> {
  const res = await request<ReverseResponse>('/reverse-geocode-locationiq', {
    method: 'POST',
    baseUrl: config.cacheBaseUrl,
    auth: false,
    body: { lat: point.lat, lon: point.lon, language: config.defaultLanguage },
  });
  return res.address ?? res.display_name ?? res.result?.address ?? '';
}
